/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useMemo, useCallback } from "react";
import { RouterProvider } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { router } from "./Router";
import { useStoreDispatch } from "./store/hooks";
import { logout } from "./store/api/auth/reducer";
import Toast from "./components/Shared/Toast/Toast";
import { useSelector } from "react-redux";
import { authenticationSelector } from "./store/api/auth/selectors";
import { accessControlSettingsSelector } from "./store/api/admin/selectors";
import { useLogoutAuditMutation } from "./store/api/auth/authApiSlice";
import { isJwtExpired } from "./utils/helpers";
import TimeoutDialog from "./components/Shared/Dialogs/TimeoutDialog";
import { setTimeoutDialog } from "./store/helpers/helperReducers";
import { DEFAULT_IDLE_TIMEOUT_MSECS, IDLE_PROMPT_WAIT_MSECS } from "./globals/globals";

import "./App.css";
import { getLastSSHActivity } from "./utils/cookie";
import { TooltipProvider } from "./components/ui/tooltip";

const App: React.FC = () => {

  const debug = false;
  const nginxServerPort = window.NGINX_SERVER_PORT;
  const isSsoEnabled = window.SSO_ENABLED === "true";
  const isNginxSSOPort = nginxServerPort === "3000";
  const redirectToSso = isSsoEnabled && isNginxSSOPort;
  const debugLog = useCallback((message: string, ...args: any[]) => {
    if (debug) {
      console.log(message, ...args);
    }
  }, [debug]);

  const SSH_POLL_INTERVAL = 10000; // Check every 10 seconds

  const dispatch = useStoreDispatch();
  const auth = useSelector(authenticationSelector);
  const [logoutAudit] = useLogoutAuditMutation();

  const logoutWithAudit = useCallback(async () => {
    if (auth?.isLoggedIn && auth?.username) {
      try {
        await logoutAudit({ username: auth.username }).unwrap();
      } catch (error) {
        debugLog("Server-side logout audit failed:", error);
      }
    }
    dispatch(logout());

    if (redirectToSso) {
      window.location.replace("/v1/login/saml2/sso");
    }
  }, [auth?.isLoggedIn, auth?.username, logoutAudit, dispatch, debugLog, redirectToSso]);

  // Get idle timeout from access control settings
  const accessControlSettings = useSelector(accessControlSettingsSelector);

  // Use useMemo to ensure these values are recalculated when dependencies change
  const idleTimeoutMinutes = useMemo(() =>
    accessControlSettings?.inactivityLogoutDurationMinutes || null,
    [accessControlSettings?.inactivityLogoutDurationMinutes]
  );

  const idleTimeoutMsec = useMemo(() =>
    idleTimeoutMinutes && idleTimeoutMinutes > 0 ? idleTimeoutMinutes * 60 * 1000 : DEFAULT_IDLE_TIMEOUT_MSECS,
    [idleTimeoutMinutes]
  );

  const isTokenExpired = useMemo(() =>
    isJwtExpired(auth?.idToken),
    [auth?.idToken]
  );

  const idleTimer = useIdleTimer({
    // A dialog will open after idleTimeoutMsec-IDLE_PROMPT_WAIT_MSECS minutes and will wait for IDLE_PROMPT_WAIT_MSECS minutes, after which it will log out automatically
    timeout: idleTimeoutMsec,
    promptBeforeIdle: IDLE_PROMPT_WAIT_MSECS,
    onPrompt: () => {
      debugLog(`🔔 Idle Timer PROMPT triggered - Total timeout: ${idleTimeoutMsec}ms (${idleTimeoutMsec / 1000 / 60}min), Prompt delay: ${IDLE_PROMPT_WAIT_MSECS}ms (${IDLE_PROMPT_WAIT_MSECS / 1000 / 60}min)`);
      if (isTokenExpired || !auth.isLoggedIn) {
        debugLog("Token expired or not logged in, logging out immediately");
        void logoutWithAudit();
      } else {
        debugLog("Showing timeout dialog to user");
        dispatch(setTimeoutDialog(true));
      }
    },
    onIdle: () => {
      debugLog(`⏰ Idle Timer IDLE triggered - Final timeout reached after ${idleTimeoutMsec}ms (${idleTimeoutMsec / 1000 / 60}min)`);
      if (!isTokenExpired && auth.isLoggedIn) {
        debugLog("Proceeding with idle timeout logout...");
        dispatch(setTimeoutDialog(false));
        void logoutWithAudit();
      }
    },
    onActive: () => {
      debugLog(`✅ User became ACTIVE - Idle timer reset. Timeout: ${idleTimeoutMsec}ms (${idleTimeoutMsec / 1000 / 60}min), Prompt at: ${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS)}ms`);
    },
    onAction: () => {
      debugLog(`🖱️ User ACTION detected - Timer reset. Next prompt in: ${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS)}ms (${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS) / 1000 / 60}min) @ ${new Date(Date.now() + (idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS)).toLocaleTimeString()}`);
    },
    debounce: 1000,
  });

  useEffect(() => {
    if (idleTimeoutMsec && idleTimeoutMsec > 0) {
      debugLog(`🔄 Idle Timer CONFIGURATION CHANGED - New timeout: ${idleTimeoutMsec}ms (${idleTimeoutMsec / 1000 / 60}min), Prompt delay: ${IDLE_PROMPT_WAIT_MSECS}ms (${IDLE_PROMPT_WAIT_MSECS / 1000 / 60}min), Prompt will show at: ${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS)}ms`);
      idleTimer.reset();
    }
  }, [idleTimeoutMsec, debugLog]);

  useEffect(() => {
    debugLog("Nginx listener port:", nginxServerPort, {
      isSsoEnabled,
      isNginxSSOPort,
      redirectToSso,
      browserLocationPort: window.location.port || "default",
    });
  }, [debugLog, nginxServerPort, isSsoEnabled, isNginxSSOPort, redirectToSso]);

  // SSH Activity Polling - Check for SSH activity every 10 seconds and reset idle timer if found
  useEffect(() => {
    if (!auth.isLoggedIn || isTokenExpired) {
      return;
    }

    const checkSSHActivity = () => {
      const lastSSHActivity = getLastSSHActivity();
      if (lastSSHActivity) {
        const timeSinceSSHActivity = Date.now() - lastSSHActivity;

        // If SSH activity is more recent than our idle timeout, reset the timer
        if (timeSinceSSHActivity <= idleTimeoutMsec) {
          debugLog(`🌐 SSH POLL: Recent SSH activity detected @ ${new Date(lastSSHActivity).toLocaleTimeString()} (${Math.round(timeSinceSSHActivity / 1000)}s ago) - Resetting idle timer`);
          dispatch(setTimeoutDialog(false));
          idleTimer.reset();
        } else {
          debugLog(`🌐 SSH POLL: Old SSH activity @ ${new Date(lastSSHActivity).toLocaleTimeString()} (${Math.round(timeSinceSSHActivity / 1000)}s ago) - No reset needed`);
        }
      } else {
        debugLog(`🌐 SSH POLL: No SSH activity detected`);
      }
    };

    // Initial check
    checkSSHActivity();

    // Set up polling interval
    const intervalId = setInterval(checkSSHActivity, SSH_POLL_INTERVAL);

    debugLog(`🌐 SSH Activity polling started - checking every ${SSH_POLL_INTERVAL / 1000}s`);

    return () => {
      clearInterval(intervalId);
      debugLog(`🌐 SSH Activity polling stopped`);
    };
  }, [auth.isLoggedIn, isTokenExpired, idleTimeoutMsec, idleTimer, debugLog, dispatch]);

  useEffect(() => {
    if (isTokenExpired) {
      void logoutWithAudit();
      //window.history.pushState({}, "", "/login");
      // window.history.replaceState({}, "", "/");
    }
  }, [isTokenExpired, logoutWithAudit]);

  const stayHandler = useCallback((value: any) => {
    debugLog(`👤 User clicked STAY LOGGED IN - Resetting idle timer. Next prompt in: ${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS)}ms (${(idleTimeoutMsec - IDLE_PROMPT_WAIT_MSECS) / 1000 / 60}min)`);
    dispatch(setTimeoutDialog(false));
    idleTimer.reset();
  }, [dispatch, idleTimer, idleTimeoutMsec, debugLog]);

  return (
    <TooltipProvider delayDuration={100}>
      <TimeoutDialog handleClickAction={stayHandler} />
      <Toast /> {/* Toast adding here to avoid unnecessary rendering */}
      <RouterProvider router={router} />
    </TooltipProvider>
  );
};

export default App;